import React, { useEffect, useState } from "react";
import styles from "./fbm-add-listing-form.module.css";
import { AddFbmListingItemForm } from "../FbmAddListingDrawer/FbmAddListingDrawer";
import { BxError, ErrorCode, ProductCondition, ShippingTemplate, SkuGenerationMode } from "@boxem/core-api-schema";
import { Product, useInventoryContext } from "@/contexts/InventoryContext";
import { useUserContext } from "@/contexts/UserContext";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import IconInfo from "@/components/icons/IconInfo";
import Autocomplete from "@/components/forms/Autocomplete/Autocomplete";
import InputNumber from "@/components/forms/InputNumber/InputNumber";
import Select from "@/components/forms/Select/Select";
import Textarea from "@/components/forms/Textarea/Textarea";
import TextInput from "@/components/forms/TextInput/TextInput";
import { ListPriceOption } from "@/app/fba/utils";
import { MockFormula, SkuFormula, SkuFormulaType } from "@/app/settings/variable_sku/variable_sku";
import { productConditionToString, categoryProbablyRequiresExpirationDate, relcalculateListPriceHelpText } from "@/app/fba/utils";
import Alert from "@/components/Alert/Alert";
import Flex from "@/components/Flex/Flex";
import Divider from "@/components/Divider/Divider";
import DatePicker from "@/components/forms/DatePicker";
import dayjs from "dayjs";

type FbmAddListingFormProps = {
  form: AddFbmListingItemForm;
  setForm: (form: AddFbmListingItemForm) => void;
  offersLoading: boolean;
  listPriceOptions: ListPriceOption[];
  setListPriceOptions: (options: ListPriceOption[]) => void;
  product: Product;
  sellerDataIndex: number;
  defaultSku: string;
  listingRestrictions: BxError[];
  loading: boolean;
  setInvalid: (val: boolean) => void,
  replenish: boolean,
};

const FbmAddListingForm = ({
  form,
  setForm,
  product,
  offersLoading,
  listPriceOptions,
  sellerDataIndex,
  defaultSku,
  listingRestrictions,
  loading,
  setInvalid,
  replenish,
}: FbmAddListingFormProps) => {
  const { allSellerSkus, suppliers } = useInventoryContext();
  const { seller } = useUserContext();
  const [listPriceHelpText, setListPriceHelpText] = useState<string>("");
  const [skuGenerationOption, setSkuGenerationOption] = useState<SkuGenerationMode>((seller?.settings?.skuGenerationOption as SkuGenerationMode) ?? SkuGenerationMode.none);
  const [formula, setFormula] = useState<SkuFormula>(!!seller?.settings?.skuGenerationFormula ? JSON.parse(seller?.settings?.skuGenerationFormula) : MockFormula);
  const [hasEditedSKU, setHasEditedSKU] = useState<boolean>(false);
  const [supplierOptions, setSupplierOptions] = useState<string[]>([]);
  const [listPriceInvalidText, setListPriceInvalidText] = useState<string | null>(null);
  const [skuInvalidText, setSkuInvalidText] = useState<string | null>(null);
  const [expirationDateInvalidText, setExpirationDateInvalidText] = useState<string | null>(null);
  const [datePurchasedInvalidText, setDatePurchasedInvalidText] = useState<string | null>(null);
  const [quantityInvalidText, setQuantityInvalidText] = useState<string | null>(null);
  const [templateInvalidText, setTemplateInvalidText] = useState<string | null>(null);
  const [templateOptions, setTemplateOptions] = useState<ShippingTemplate[]>([]);

  const visualErrors: { message: string; link?: string; linkDescription?: string }[] = [];
  if (listingRestrictions.length) {
    listingRestrictions?.forEach((error) => {
      let message;
      switch (error.type) {
        case ErrorCode.DUPLICATE_ITEM:
          message = "This product is a duplicate.";
          break;
  
        default:
          message = error.message;
          break;
      }
  
      visualErrors.push({
        message: message,
        link: error.actions?.[0]?.link,
        linkDescription: error?.actions?.[0]?.description,
      });
    });
  }
  if (!!product?.hazmat) {
    visualErrors.push({
      message: "This product is classified as dangerous goods (hazmat), which means it is or contains substances or materials that may pose a risk during storing, handling, or transporting. It may not be elligible to be sent FBM.",
      link: "https://sell.amazon.com/blog/amazon-hazmat-dangerous-goods",
      linkDescription: "Read More about Dangerous Goods on Amazon", 
    });
  }

  const generateFormulaSKU = (skuGenerationCount: number, formRef: AddFbmListingItemForm) => {
    let s = "";
    for (let i = 0; i < formula.length; i++) {
      const type = formula[i].type;
      let field = "";
      switch (type) {
        case SkuFormulaType.buyCost:
          field = formRef?.buyCost?.toString() ?? "";
          break;
        case SkuFormulaType.supplier:
          field = formRef.supplier ?? "";
          break;
        case SkuFormulaType.purchaseDate:
          const datePurchased = formRef.datePurchased;
          field = datePurchased ? dayjs(datePurchased).format("MM/DD/YYYY") : "";
          break;
        case SkuFormulaType.salesRank:
          field = product.salesRank ? product?.salesRank?.toString() : "";
          break;
        case SkuFormulaType.listPrice:
          field = formRef.price?.price?.toString() ?? "";
          break;
        case SkuFormulaType.asin:
          field = product.asin;
          break;
        case SkuFormulaType.count:
          field =  skuGenerationCount?.toString() ?? "0";
          break;
        case SkuFormulaType.condition:
          const condition = (formRef.condition ?? "");
          field = productConditionToString(condition as ProductCondition).replaceAll(" ", "");
          break;
        case SkuFormulaType.custom:
          field = formula[i].string;
          break;
      }
      s += field;
      if (i !== formula.length - 1 && field !== "") s += "-";
    }
    formRef.sku = s.replaceAll(" ", "");
    setForm({...formRef});
  };

  const onChange = (form: AddFbmListingItemForm) => {
    if (skuGenerationOption === SkuGenerationMode.variable && !hasEditedSKU) {
      generateFormulaSKU(seller.settings.skuGenerationCount, form);
    } else {
      setForm(form);
    }
  };

  useEffect(() => {
    setForm({
      ...form,
      sku: defaultSku,
      shippingTemplateId: seller?.shippingTemplates[0]?.id,
    });
    setSupplierOptions(suppliers.map((supplier) => supplier.name));
    setTemplateOptions(seller?.shippingTemplates);
  }, [suppliers, seller]);

  useEffect(() => {
    setInvalid(!!(templateInvalidText || quantityInvalidText ||skuInvalidText || expirationDateInvalidText || datePurchasedInvalidText || listPriceInvalidText));
  }, [templateInvalidText, quantityInvalidText, skuInvalidText, expirationDateInvalidText, datePurchasedInvalidText, listPriceInvalidText]);

  useEffect(() => {
    if (offersLoading) return; // we need to know the offers before being able to do this.

    if (listPriceOptions.length !== 0) {
      const buyBox = listPriceOptions.find((a) => a.isBuyBox && a.fba);

      //If there is no buy-box then default to lowest fba price.
      if (buyBox) {
        form.price = {
          price: buyBox.price,
        }

        setForm({...form});
        setListPriceHelpText(relcalculateListPriceHelpText(offersLoading, parseFloat(form.price.price), listPriceOptions));
      } else {
        form.price = {
          price: Math.min(...listPriceOptions.map(option => parseFloat(option.price))).toString(),
        }
        setForm({...form});
        setListPriceHelpText(relcalculateListPriceHelpText(offersLoading, parseFloat(form.price.price), listPriceOptions));
      }
    };
  }, [listPriceOptions, offersLoading]);

  useEffect(() => {
    {
      if (!form.price?.price) {
        setListPriceInvalidText("List price is required");
      } else {
        setListPriceInvalidText(null);  
      }
    }

    {
      if (!form.quantity || form.quantity <= 0) {
        setQuantityInvalidText("Quantity must be above 0");
      } else {
        setQuantityInvalidText(null);
      }
    }

    {
      if (!form.shippingTemplateId || form.shippingTemplateId === 0) {
        setTemplateInvalidText("You must select a shipping template")
      } else {
        setTemplateInvalidText(null);
      }
    }

    {
      if (!form.sku || form.sku.trim().length < 1) {
        setSkuInvalidText("SKU is required!");
      } else if (form.sku.length > 40) {
        setSkuInvalidText("SKU must be 40 characters or less.");
      } else if (!/^[\x20-\x7E]*$/.test(form?.sku)) {
        setSkuInvalidText("SKU is invalid - contains invalid character(s).");
      } else if ((!product.sellerData || defaultSku !== product.sellerData[sellerDataIndex].sku) && allSellerSkus.has(form?.sku) && hasEditedSKU && !replenish) {
        setSkuInvalidText("SKU is already in use!");
      } else {
        setSkuInvalidText(null);
      }
    }

    {
      if (!!form.datePurchased) {
        if (new Date(form.datePurchased) > new Date()) {
          setDatePurchasedInvalidText("If provided, date purchased must be in the past.");
        } else {
          setDatePurchasedInvalidText(null);
        }
      } else {
        setDatePurchasedInvalidText(null);
      }
    }

    {
      const expirationDateRequired = categoryProbablyRequiresExpirationDate(product.category);
      if (!!form.expirationDate) {
        const nintetyDaysInFuture = new Date();
        nintetyDaysInFuture.setDate(nintetyDaysInFuture.getDate() + 90);
        if (new Date(form.expirationDate) < nintetyDaysInFuture) {
          setExpirationDateInvalidText("If provided, expiration date must be at least 90 days in the future.");
        } else {
          setExpirationDateInvalidText(null);
        }
      } else {
        if (expirationDateRequired) {
          setExpirationDateInvalidText(`Items in the category '${product.category}' require an expiration date.`);
        } else {
          setExpirationDateInvalidText(null);
        }
      }
    }
  }, [form]);

  return (
    <div className={styles.fbmListingFormContainer}>
      {visualErrors.length > 0 && (
        <Alert
          className={styles.addItemErrorAlert}
          type="error"
          message={
            <>
              {visualErrors.map((ve, i) => {
                return (
                  <Flex key={i} direction="column" gap={2}>
                    <span>{ve.message}</span>

                    {ve.link && (
                      <a href={ve.link} target="_blank">
                        {ve.linkDescription}
                      </a>
                    )}
                  </Flex>
                );
              })}
            </>
          }
        />
      )}

      <h1>Pricing & Profit</h1>

      <div className={styles.inputGroup}>
        <label htmlFor="listPrice">
          <Flex gap={1.5} align="center">
            <span>List price:</span>
            <Tooltip
              title="List price is the amount a potential buyer will see on the listing for this item."
            >
              <IconInfo style={{ marginLeft: "0.5ch" }} />
            </Tooltip>
          </Flex>
        </label>

        <Flex direction="column" gap={2}>
          <Autocomplete
            prefix="$"
            type="number" //This does not make the "text input" a "number input". It changes the validation that happens and allows users to enter decimals.
            inputId="listPrice"
            disabled={loading}
            invalid={!!listPriceInvalidText}
            loading={offersLoading}
            placeholder={listPriceOptions.length === 0 ? "Type a list price" : "Type or select a list price"}
            value={form.price}
            onChange={(option) => onChange({...form, price: option })}
            onTextChange={text => onChange({...form, price: { price: text }})}
            options={listPriceOptions}
            optionToDisplayString={option => option.price?.toString()}
            optionRender={(option) => {
              return (
                <Flex justify="space-between" style={{width:"100%"}}>
                  <span>${option.price}</span>
                  <Flex gap={2}>
                    {(option.isBuyBox && option.fba) && (
                      <Tooltip title="This is the FBA buy-box for this item">
                        <img src="/images/buy-box.svg" width={24} height={24} alt="buy box" />
                      </Tooltip>
                    )}
                    {option.isAmazon && (
                      <Tooltip title="Amazon is a seller on the listing">
                        <img src="/images/amazon.svg" width={24} height={24} alt="amazon" />
                      </Tooltip>
                    )}
                    <Tooltip title={option.fba ? "Fulfilled by Amazon" : "Fulfilled by Merchant"}>
                      <span className="weak">{option.fba ? "FBA" : "FBM"}</span>
                    </Tooltip>
                  </Flex>
                </Flex>
              );
            }}
          />
          {listPriceInvalidText && <p className={`small-text ${styles.invalidText}`}>{listPriceInvalidText}</p>}
        </Flex>
      </div>

      {(!listPriceInvalidText && listPriceHelpText) && (<span className="small-text weak">{listPriceHelpText}</span>)}

      <div className={styles.inputGroup}>
        <label htmlFor="buyCost">
          <Flex gap={1.5} align="center">
            <span>Buy cost:</span>
            <Tooltip
              title="Buy cost is the total amount you paid PER UNIT of this item. We store this for you for analytics purposes"
            >
              <IconInfo style={{ marginLeft: "0.5ch" }} />
            </Tooltip>
          </Flex>
        </label>
        <InputNumber
          id="buyCost"
          disabled={loading}
          prefix="$"
          showControls={false}
          centerText={false}
          value={form.buyCost}
          onChange={v => onChange({...form, buyCost: v})}
        />
      </div>

      <div className={styles.inputGroup}>
        <label htmlFor="quantity">
          {replenish ? (
            <Flex gap={1.5} align="center">
              <span>Quantity:</span>
              <Tooltip
                title="Because you are replenishing this listing, the quantity represents how many items to add and NOT what the total inventory should be"
              >
                <IconInfo style={{ marginLeft: "0.5ch" }} />
              </Tooltip>
            </Flex>
          ) : (
            <>
              Quantity:
            </>
          )}
        </label>
        <InputNumber
          id="quantity"
          disabled={loading}
          invalid={!!quantityInvalidText}
          min={1}
          max={10000}
          value={form.quantity}
          onChange={v => onChange({...form, quantity: v})}
        />
      </div>

      {quantityInvalidText && <p className={`small-text ${styles.invalidText}`}>{quantityInvalidText}</p>}

      <div className={styles.inputGroup}>
        <label htmlFor="shipping-template">
          Template:
        </label>
        <Select
          id="shipping-template"
          disabled={loading}
          fullWidth
          options={templateOptions}
          optionRender={option => option.name}
          value={templateOptions.find(opt => opt.id === form.shippingTemplateId)}
          onChange={v => onChange({...form, shippingTemplateId: v.id})}
        />
      </div>

      {templateInvalidText && <p className={`small-text ${styles.invalidText}`}>{templateInvalidText}</p>}

      <Divider />

      <h1>Supplier info</h1>

      <div className={styles.inputGroup}>
        <label htmlFor="supplier">
          Supplier:
        </label>
        <Autocomplete
          inputId="supplier"
          disabled={loading}
          options={supplierOptions}
          placeholder={supplierOptions.length === 0 ? "Type a supplier name" : "Type or select a supplier name"}
          value={form.supplier}
          onChange={v => {
            onChange({...form, supplier: v});
          }}
          onCreateNewOption={v => {
            setSupplierOptions([ ...supplierOptions, v]);
            onChange({...form, supplier: v});
          }}
          showSearchIcon
        />
      </div>

      <div className={styles.inputGroup}>
        <label htmlFor="datePurchased">
          Date purchased:
        </label>
        <Flex direction="column" gap={2}>
          <DatePicker
            id="datePurchased"
            disabled={loading}
            value={form.datePurchased ? new Date(form.datePurchased) : null}
            onChange={v => onChange({...form, datePurchased: v.toISOString()})}
          />
          {datePurchasedInvalidText && (<div className={`small-text ${styles.invalidText}`}>{datePurchasedInvalidText}</div>)}
        </Flex>
      </div>

      <Divider />

      <h1>Product details</h1>

      <div className={styles.inputGroup}>
        <label htmlFor="condition">
          Condition:
        </label>
        <Select
          id="condition"
          disabled={loading}
          fullWidth
          options={[
            ProductCondition.New,
            ProductCondition.NewOpenBox,
            ProductCondition.NewOEM,

            ProductCondition.Refurbished,

            ProductCondition.UsedLikeNew,
            ProductCondition.UsedVeryGood,
            ProductCondition.UsedGood,
            ProductCondition.UsedAcceptable,

            ProductCondition.CollectibleLikeNew,
            ProductCondition.CollectibleVeryGood,
            ProductCondition.CollectibleGood,
            ProductCondition.CollectibleAcceptable,

            ProductCondition.Club,
          ]}
          optionRender={option => {
            switch (option) {
              case ProductCondition.Refurbished:
              case ProductCondition.UsedLikeNew:
              case ProductCondition.CollectibleLikeNew:
              case ProductCondition.Club:
                return (
                  <Flex direction="column" gap={1} style={{width:"100%"}}>
                    <Divider margin="0 0 0.5rem 0" />
                    {productConditionToString(option, false)}
                  </Flex>
                );
            }

            return productConditionToString(option, false);
          }}
          selectionRender={option => productConditionToString(option, false)}
          value={form.condition}
          onChange={v => onChange({...form, condition: v})}
        />
      </div>

      {(form.condition !== ProductCondition.New) && (
        <div className={styles.conditionNotes}>
          <div className={styles.conditionNotesLabel}>
            <label htmlFor="conditionNotes">
              Notes:
            </label>
          </div>
          <Textarea
            inputId="conditionNotes"
            disabled={loading}
            placeholder="(optional)"
            rows={6}
            maxLength={1000}
            value={form.conditionNotes}
            onChange={(v) => onChange({...form, conditionNotes: v})}
          />
        </div>
      )}

      <div className={styles.inputGroup}>
        <label htmlFor="sku">
          <Flex gap={1.5} align="center">
            <span>SKU:</span>
            <Tooltip
              title="The SKU is a per-seller unique identifier for a sellable product. Configure your SKU here, and set up rules for automatic SKU generation inside Variable SKU settings"
            >
              <IconInfo style={{ marginLeft: "0.5ch" }} />
            </Tooltip>
          </Flex>
        </label>
        <Flex direction="column" gap={2}>
          <TextInput
            inputId="sku"
            invalid={!!skuInvalidText}
            disabled={loading}
            value={form.sku}
            onChange={v => {
              if (!hasEditedSKU) setHasEditedSKU(true);
              setForm({...form, sku: v});
            }} 
          />
          {skuInvalidText && (<div className={`small-text ${styles.invalidText}`}>{skuInvalidText}</div>)}
        </Flex>
      </div>

      <div className={styles.inputGroup}>
        <label htmlFor="expirationDate">
          Expiration date:
        </label>
        <Flex direction="column" gap={2}>
          <DatePicker
            id="expirationDate"
            disabled={loading}
            value={form.expirationDate ? new Date(form.expirationDate) : null}
            onChange={v => onChange({...form, expirationDate: v.toISOString()})}
          />
          {expirationDateInvalidText && (<div className={`small-text ${styles.invalidText}`}>{expirationDateInvalidText}</div>)}
        </Flex>
      </div>
    </div>
  );
};

export default FbmAddListingForm;